// This file is part of re-motion
// Copyright (c) rubicon IT GmbH, www.rubicon.eu
// 
import { JsonRpcErrorCode } from "@lib/messaging/rpc/jsonRpc/JsonRpcErrorCode";
import { RpcErrorCode } from "@lib/messaging/rpc/RpcErrorCode";
import { Assert } from "@lib/utilities/Assert";

/**
 * Represents an error that occurred during an RPC call.
 */
export class RpcError<T>
{
  static canceled = new RpcError<void>(RpcErrorCode.Canceled, "Canceled.", undefined);
  static internal = new RpcError<void>(JsonRpcErrorCode.InternalError, "Internal error.", undefined);
  static timeout = new RpcError<void>(RpcErrorCode.Timeout, "Timeout.", undefined);
  static unauthorized = new RpcError<void>(RpcErrorCode.Unauthorized, "Unauthorized.", undefined);
  static unknown = new RpcError<void>(RpcErrorCode.Unknown, "Unknown.", undefined);

  static createCustom(message: string): RpcError<void>;
  static createCustom<T>(message: string, data: T): RpcError<T>;
  static createCustom<T>(message: string, data?: T): RpcError<T>
  {
    return new RpcError<any>(RpcErrorCode.Custom, message, data);
  }

  static createJsonRpc(errorCode: JsonRpcErrorCode): RpcError<any>
  {
    switch (errorCode)
    {
      case JsonRpcErrorCode.InternalError:
        return new RpcError<any>(JsonRpcErrorCode.InternalError, "Internal error.", undefined);
      case JsonRpcErrorCode.InvalidParameters:
        return new RpcError<any>(JsonRpcErrorCode.InvalidParameters, "Invalid parameters.", undefined);
      case JsonRpcErrorCode.InvalidRequest:
        return new RpcError<any>(JsonRpcErrorCode.InvalidRequest, "Invalid request.", undefined);
      case JsonRpcErrorCode.MethodNotFound:
        return new RpcError<any>(JsonRpcErrorCode.InvalidRequest, "Method not found.", undefined);
      case JsonRpcErrorCode.ParseError:
        return new RpcError<any>(JsonRpcErrorCode.ParseError, "Parse error.", undefined);
      default:
        return Assert.ensureNever(errorCode);
    }
  }

  static createMethodNotFound(method: string): RpcError<any>
  {
    return new RpcError<any>(JsonRpcErrorCode.MethodNotFound, `Method '${method}' not found.`, undefined);
  }

  static createRaw(code: number, message: string): RpcError<void>;
  static createRaw<T>(code: number, message: string, data: T): RpcError<T>;
  static createRaw<T>(code: number, message: string, data?: T): RpcError<T>
  {
    return new RpcError<T>(code, message, data);
  }

  readonly code: number;
  readonly message: string;
  readonly data?: T;

  private constructor(code: number, message: string, data?: T)
  {
    this.code = code;
    this.message = message;
    this.data = data;
  }
}